import type { AutomationLinksFromDescriptionsDto } from '@tms/shared';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button, Dialog } from '../../../components/ui';
import { useErrorText, useT } from '../../../i18n';
import { useLinksFromDescriptions } from './api';

/**
 * Admins: the «Автотесты в GitLab» lines written into case descriptions on
 * 27.10 become real links. `start` only shows what would be linked; the dialog
 * creates them. The dialog is rendered by the page, so it outlives the menu
 * item that starts it.
 */
export const useLinksFromDescriptionsAction = (): {
  start: () => void;
  pending: boolean;
  dialog: ReactNode;
} => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const links = useLinksFromDescriptions();
  const [preview, setPreview] = useState<AutomationLinksFromDescriptionsDto | null>(null);

  const start = () =>
    links.mutate(false, {
      onSuccess: setPreview,
      onError: (error) => toast.error(errorText(error)),
    });
  const apply = () =>
    links.mutate(true, {
      onSuccess: (result) => {
        toast.success(t('automation.caseLinks.fromDescriptionsDone', { count: result.created }));
        setPreview(null);
      },
      onError: (error) => toast.error(errorText(error)),
    });

  const total = preview?.cases.reduce((sum, c) => sum + c.links.length, 0) ?? 0;
  const dialog = (
    <Dialog
      open={preview !== null}
      onOpenChange={(next) => {
        if (!next) setPreview(null);
      }}
      size="lg"
      title={t('automation.caseLinks.fromDescriptionsTitle')}
      description={t('automation.caseLinks.fromDescriptionsHint')}
      footer={
        <>
          <Button onClick={() => setPreview(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={total === 0 || links.isPending} onClick={apply}>
            {t('automation.caseLinks.fromDescriptionsApply')}
          </Button>
        </>
      }
    >
      {preview && preview.cases.length === 0 ? (
        <p className="text-[13px] text-ink-muted">
          {t('automation.caseLinks.fromDescriptionsEmpty')}
        </p>
      ) : (
        <ul className="divide-y divide-border text-[13px]">
          {preview?.cases.map((c) => (
            <li key={c.caseKey} className="space-y-1 py-2">
              <p>
                <span className="font-mono font-semibold">{c.caseKey}</span>{' '}
                <span className="text-ink-muted">{c.title}</span>
              </p>
              {c.links.map((l) => (
                <p
                  key={`${l.automationProjectId}-${l.className}-${l.testMethod ?? ''}`}
                  className="pl-4 font-mono text-[12px]"
                >
                  {l.automationProjectId} / {l.className}
                  {l.testMethod ? `.${l.testMethod}` : ''}
                </p>
              ))}
              {c.unmatched.length > 0 ? (
                <p className="pl-4 text-[12px] text-block">
                  {t('automation.caseLinks.unmatched', { names: c.unmatched.join(', ') })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
  return { start, pending: links.isPending, dialog };
};
